import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Row, Col, Form, Button, Alert, Badge, ListGroup } from 'react-bootstrap';
import { DollarSign, Video, BarChart, Settings, Shield, Lock } from 'react-feather';
import MonetizationService from '../../../services/MonetizationService';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';

const ALL_FEATURES = {
  allow_avod: true, allow_svod: true, allow_ads: true,
  allow_google_ads: true, allow_programmatic_ads: true,
  allow_preroll: true, allow_midroll: true, allow_postroll: true,
};

const MonetizationSettings = () => {
  const isAdmin = useSelector(selectIsAdmin);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [features, setFeatures] = useState(null);
  const [settings, setSettings] = useState({
    enable_avod: false,
    enable_svod: false,
    enable_ads: false,
    google_ad_client_id: '',
    google_ad_slot_id: '',
    programmatic_ad_partner: '',
    vast_tag_url: '',
    preroll_enabled: false,
    midroll_enabled: false,
    postroll_enabled: false,
    ad_frequency_seconds: 300,
    skip_preroll_seconds: 0
  });

  useEffect(() => {
    loadMonetizationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadMonetizationSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await MonetizationService.getMonetizationSettings();
      
      if (response.success) {
        setFeatures(isAdmin ? { ...response.features, ...ALL_FEATURES } : response.features);
        if (response.settings) {
          setSettings({
            enable_avod: response.settings.enable_avod || false,
            enable_svod: response.settings.enable_svod || false,
            enable_ads: response.settings.enable_ads || false,
            google_ad_client_id: response.settings.google_ad_client_id || '',
            google_ad_slot_id: response.settings.google_ad_slot_id || '',
            programmatic_ad_partner: response.settings.programmatic_ad_partner || '',
            vast_tag_url: response.settings.vast_tag_url || '',
            preroll_enabled: response.settings.preroll_enabled || false,
            midroll_enabled: response.settings.midroll_enabled || false,
            postroll_enabled: response.settings.postroll_enabled || false,
            ad_frequency_seconds: response.settings.ad_frequency_seconds || 300,
            skip_preroll_seconds: response.settings.skip_preroll_seconds || 0
          });
        }
      } else {
        setError(response.error || 'Failed to load monetization settings');
      }
    } catch (err) {
      console.error('Error loading monetization settings:', err);
      setError(err.message || 'Failed to load monetization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await MonetizationService.updateMonetizationSettings(settings);
      
      if (response.success) {
        setSuccess('Monetization settings saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving monetization settings:', err);
      setError(err.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading monetization settings...</p>
      </div>
    );
  }

  const hasAnyMonetizationFeature = isAdmin || (features && (
    features.allow_avod ||
    features.allow_svod ||
    features.allow_ads ||
    features.allow_google_ads ||
    features.allow_programmatic_ads
  ));

  return (
    <div className="monetization-settings">
      <h4 className="mb-4">
        <DollarSign className="me-2" />
        Monetization Settings
      </h4>

      {!hasAnyMonetizationFeature && (
        <Alert variant="info">
          <Lock className="me-2" />
          Your current subscription plan does not include monetization features.
          <Button variant="link" href="/pricing" className="p-0 ms-2">
            Upgrade to a higher plan to unlock monetization features
          </Button>
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {hasAnyMonetizationFeature && (
        <>
          {/* Available Features */}
          <Card className="mb-4">
            <Card.Header>
              <BarChart className="me-2" />
              Your Plan Features
            </Card.Header>
            <Card.Body>
              <Row>
                {features?.allow_avod && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    AVOD (Advertising VOD)
                  </Col>
                )}
                {features?.allow_svod && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    SVOD (Subscription VOD)
                  </Col>
                )}
                {features?.allow_ads && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    Video Advertising
                  </Col>
                )}
                {features?.allow_preroll && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    Pre-roll Ads
                  </Col>
                )}
                {features?.allow_midroll && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    Mid-roll Ads
                  </Col>
                )}
                {features?.allow_postroll && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    Post-roll Ads
                  </Col>
                )}
                {features?.allow_google_ads && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    Google Ad Manager
                  </Col>
                )}
                {features?.allow_programmatic_ads && (
                  <Col md={4} className="mb-2">
                    <Badge bg="success" className="me-2">✓</Badge>
                    Programmatic Ads
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>

          {/* AVOD Settings */}
          {features?.allow_avod && (
            <Card className="mb-4">
              <Card.Header>
                <Video className="me-2" />
                AVOD (Advertising Video on Demand)
              </Card.Header>
              <Card.Body>
                <Form.Check
                  type="switch"
                  id="enable-avod"
                  name="enable_avod"
                  label="Enable AVOD - Show ads on your VOD content"
                  checked={settings.enable_avod}
                  onChange={handleChange}
                  className="mb-3"
                />
                <Form.Group>
                  <Form.Label>VAST Tag URL (Optional)</Form.Label>
                  <Form.Control
                    type="url"
                    name="vast_tag_url"
                    placeholder="https://your-ad-server.com/vast.xml"
                    value={settings.vast_tag_url}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Enter your VAST/VPAID ad tag URL for custom ad serving
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          )}

          {/* SVOD Settings */}
          {features?.allow_svod && (
            <Card className="mb-4">
              <Card.Header>
                <Shield className="me-2" />
                SVOD (Subscription Video on Demand)
              </Card.Header>
              <Card.Body>
                <Form.Check
                  type="switch"
                  id="enable-svod"
                  name="enable_svod"
                  label="Enable SVOD - Offer premium subscription content"
                  checked={settings.enable_svod}
                  onChange={handleChange}
                />
              </Card.Body>
            </Card>
          )}

          {/* Ad Placement Settings */}
          {features?.allow_ads && (
            <Card className="mb-4">
              <Card.Header>
                <BarChart className="me-2" />
                Ad Placement Settings
              </Card.Header>
              <Card.Body>
                <Form.Check
                  type="switch"
                  id="enable-ads"
                  name="enable_ads"
                  label="Enable Video Ads"
                  checked={settings.enable_ads}
                  onChange={handleChange}
                  className="mb-3"
                />

                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enable-preroll"
                      name="preroll_enabled"
                      label="Pre-roll Ads"
                      checked={settings.preroll_enabled}
                      onChange={handleChange}
                      disabled={!features?.allow_preroll}
                    />
                    {!features?.allow_preroll && (
                      <Form.Text className="text-muted">
                        Upgrade to Enterprise for pre-roll ads
                      </Form.Text>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enable-midroll"
                      name="midroll_enabled"
                      label="Mid-roll Ads"
                      checked={settings.midroll_enabled}
                      onChange={handleChange}
                      disabled={!features?.allow_midroll}
                    />
                    {!features?.allow_midroll && (
                      <Form.Text className="text-muted">
                        Upgrade to Enterprise for mid-roll ads
                      </Form.Text>
                    )}
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enable-postroll"
                      name="postroll_enabled"
                      label="Post-roll Ads"
                      checked={settings.postroll_enabled}
                      onChange={handleChange}
                      disabled={!features?.allow_postroll}
                    />
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Ad Frequency (seconds)</Form.Label>
                      <Form.Control
                        type="number"
                        name="ad_frequency_seconds"
                        value={settings.ad_frequency_seconds}
                        onChange={handleChange}
                        min={60}
                        max={3600}
                      />
                      <Form.Text className="text-muted">
                        How often to show mid-roll ads (60-3600 seconds)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Skip Pre-roll (seconds)</Form.Label>
                      <Form.Control
                        type="number"
                        name="skip_preroll_seconds"
                        value={settings.skip_preroll_seconds}
                        onChange={handleChange}
                        min={0}
                        max={30}
                      />
                      <Form.Text className="text-muted">
                        Allow users to skip pre-roll after X seconds
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Google Ad Manager Settings */}
          {features?.allow_google_ads && (
            <Card className="mb-4">
              <Card.Header>
                <Settings className="me-2" />
                Google Ad Manager Integration
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Google Ad Client ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="google_ad_client_id"
                        placeholder="ca-pub-xxxxxxxxxxxxxxxx"
                        value={settings.google_ad_client_id}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Google Ad Slot ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="google_ad_slot_id"
                        placeholder="1234567890"
                        value={settings.google_ad_slot_id}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Programmatic Ads Settings */}
          {features?.allow_programmatic_ads && (
            <Card className="mb-4">
              <Card.Header>
                <DollarSign className="me-2" />
                Programmatic Ad Partners
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Ad Partner Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="programmatic_ad_partner"
                    placeholder="e.g., PubMatic, OpenX, Magnite"
                    value={settings.programmatic_ad_partner}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Enter your programmatic ad partner name
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          )}

          {/* Save Button */}
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default MonetizationSettings;

